import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";
import InfoIcon from "@mui/icons-material/Info";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";

const drawerWidth = 240;

// ─── Small formatting/sanitizing helpers ───────────────────────────────────
const sanitizeName = (name) =>
  (name ?? "")
    .replace(/[`'"]/g, "") // strip stray backticks/quotes from bad DB entries
    .trim();

// Normalizes any value used as a lookup key (examType / subExam names).
// CRITICAL: different endpoints represent "no sub-exam" inconsistently —
// GetExamResults uses subExam: "", while GetExamSubCategory_Result returns
// subExam: "--" for the same case. Without unifying these two placeholders
// into the same key, "" !== "--" and every marks lookup for single-category
// exam types (e.g. Term 1) silently fails AND a duplicate/ghost column gets
// created by the backfill safety-net below — which is exactly the bug seen
// in the PDF (two "Term 1" sub-columns, one always blank).
const normKey = (val) => {
  const s = (val ?? "").toString().trim();
  return s === "--" || s === "-" ? "" : s;
};

const formatPercent = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(val)) return "N/A";
  return `${Number(val).toFixed(2)}%`;
};

const formatMarks = (val) => {
  if (val === null || val === undefined || val === "") return "--";
  const num = Number(val);
  return isNaN(num) ? "--" : (Number.isInteger(num) ? String(num) : num.toFixed(2));
};

// Color used for grade text/badges throughout the PDF (regular, non
// pre-primary report). Matches the current school-wide grading scale:
//   A+ / A  -> green   B+ / B -> blue-teal   C -> amber   F -> red
const gradeColor = (grade) => {
  switch ((grade || "").toString().trim().toUpperCase()) {
    case "A+":
    case "A":
      return [22, 130, 60]; // green
    case "B+":
    case "B":
      return [24, 100, 140]; // blue-teal
    case "C":
      return [184, 134, 11]; // amber
    case "F":
      return [178, 34, 34]; // red
    default:
      return [60, 60, 60]; // neutral gray
  }
};

// Marks-percentage → letter grade, per the school's current grading policy:
//   90 and above => A+, 80-89 => A, 70-79 => B+, 60-69 => B, 50-59 => C, below 50 => F
// Used as a fallback wherever the API gives us marks but no totalGrade.
const gradeFromPercent = (pct) => {
  if (pct === null || pct === undefined || pct === "" || isNaN(pct)) return "N/A";
  const p = Number(pct);
  if (p >= 90) return "A+";
  if (p >= 80) return "A";
  if (p >= 70) return "B+";
  if (p >= 60) return "B";
  if (p >= 50) return "C";
  return "F";
};

// Chip color (MUI) used for grade badges on-screen
const gradeChipColor = (grade) => {
  switch ((grade || "").toString().trim().toUpperCase()) {
    case "A+":
    case "A":
      return "success";
    case "B+":
    case "B":
      return "info";
    case "C":
      return "warning";
    case "F":
      return "error";
    default:
      return "default";
  }
};

// Maps API monthId (1-12) to a display name. Used only in the fee-due
// breakdown modal.
const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDueDate = (val) => {
  if (!val) return "--";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString();
};

// ─── Pre-primary (LKG / UKG / Nursery) detection ───────────────────────────
// These classes get the "Academic performance: Scholastic Areas" report card
// instead of the regular numeric marks matrix.
const PRE_PRIMARY_CLASS_TOKENS = ["NUR", "LKG", "UKG"];
const isPrePrimaryClass = (className) => {
  if (!className) return false;
  const normalized = className.toString().trim().toUpperCase();
  return PRE_PRIMARY_CLASS_TOKENS.some((token) => normalized.includes(token));
};

// ─── Pre-primary "Academic performance: Scholastic Areas" data builder ─────
// Groups indicator rows by subject ("quotient"), e.g.:
//   English Socio Quotient  -> Recitation, Show & Tell
//   Hindi Quotient          -> Oral Work, Written
//
// For each subject, indicator rows are built from that subject's OWN entries
// in `marksGradeData` (examType/subExam combos that actually have a grade
// recorded for that subject) rather than from the flat, subject-agnostic
// `examHeadersData` column list — using the shared column list would collide
// two different subjects' indicators that happen to share a name (e.g. "Oral
// Work" under both Hindi and Numeric Quotient) into a single merged row.
//
// Grades are NOT computed here — each indicator's grade is whatever the
// backend already returned on that result row, looked up via marksGradeData.
// Renders the "Academic performance: Scholastic Areas" section (title +
// Overall Marks/Percentage/Grade strip + a Subject x [ExamType > SubExam]
// matrix table — same layout/columns as the regular numeric marks table,
// except every cell holds the backend's GRADE for that exam/sub-exam
// instead of obtained marks, and there is no Total(Obt/Max) column since
// pre-primary has no marks to sum). Returns the Y position to continue
// drawing from.
const addPrePrimaryScholasticSection = (doc, ctx) => {
  const {
    pageWidth,
    minMargin,
    startY,
    NAVY,
    TEAL,
    LIGHT_BG,
    subjectNames,
    examHeadersData,
    marksGradeData,
    subjectTotalsMap,
    overallMarksText,
    overallPercentageText,
    overallGradeText,
  } = ctx;

  let y = startY;
  const tableWidth = pageWidth - 2 * minMargin;

  // Title
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  doc.text("Academic performance: Scholastic Areas", minMargin, y);
  y += 5;

  // Overall Marks | Percentage | Grade summary strip
  doc.autoTable({
    startY: y,
    body: [
      [
        { content: "Overall Marks", styles: { fontStyle: "bold" } },
        { content: overallMarksText },
        { content: "Percentage", styles: { fontStyle: "bold" } },
        { content: overallPercentageText },
        { content: "Grade", styles: { fontStyle: "bold" } },
        { content: overallGradeText, styles: { fontStyle: "bold", textColor: gradeColor(overallGradeText) } },
      ],
    ],
    styles: { fontSize: 9.5, font: "times", cellPadding: 3, halign: "left" },
    theme: "grid",
    margin: { left: minMargin, right: minMargin },
    tableWidth,
  });
  y = doc.lastAutoTable.finalY + 4;

  // ── Matrix table: Subject x [ExamType > SubExam] -> Grade. Same layout as
  // the regular numeric marks table, but cells hold GRADE instead of marks,
  // and there's no Total(Obt/Max) column since pre-primary has no marks.
  const headerRow1 = [
    { content: "S.No", rowSpan: 2, styles: { halign: "center" } },
    { content: "Subject", rowSpan: 2, styles: { halign: "center" } },
  ];
  const headerRow2 = [];

  examHeadersData.forEach((exam) => {
    headerRow1.push({
      content: exam.examType,
      colSpan: exam.subCategories.length,
      styles: { halign: "center" },
    });
    exam.subCategories.forEach((sub) => {
      const label = sub.name ? sub.name : "Grade";
      headerRow2.push({ content: label, styles: { halign: "center" } });
    });
  });

  headerRow1.push({ content: "Overall\nGrade", rowSpan: 2, styles: { halign: "center" } });

  const tableHeaders = [headerRow1, headerRow2];

  const tableData = subjectNames.map((subject, idx) => {
    const row = [idx + 1, subject];

    examHeadersData.forEach((exam) => {
      exam.subCategories.forEach((sub) => {
        const grade = marksGradeData[subject]?.[exam.examType]?.[sub.name];
        row.push(grade || "--");
      });
    });

    row.push(subjectTotalsMap[subject]?.totalGrade ?? "--");
    return row;
  });

  const fixedColsCount = 3; // S.No + Subject + Overall Grade
  const totalColumns =
    2 + examHeadersData.reduce((sum, e) => sum + e.subCategories.length, 0) + 1;
  const gradeColIndex = totalColumns - 1;

  const snoColW = 10;
  const subjectColW = 34;
  const gradeColW = 22;
  const available = pageWidth - 2 * minMargin - snoColW - subjectColW - gradeColW;
  const subExamColW = Math.min(24, available / (totalColumns - fixedColsCount));
  const matrixWidth = snoColW + subjectColW + (totalColumns - fixedColsCount) * subExamColW + gradeColW;
  const marginLeft = (pageWidth - matrixWidth) / 2;

  const columnStyles = {
    0: { cellWidth: snoColW },
    1: { cellWidth: subjectColW },
  };
  let colIdx = 2;
  examHeadersData.forEach((exam) => {
    exam.subCategories.forEach(() => {
      columnStyles[colIdx++] = { cellWidth: subExamColW };
    });
  });
  columnStyles[colIdx] = { cellWidth: gradeColW };

  doc.autoTable({
    startY: y,
    head: tableHeaders,
    body: tableData,
    styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 1.6 },
    headStyles: {
      fillColor: NAVY,
      textColor: [255, 255, 255],
      lineWidth: 0.5,
      font: "times",
      fontStyle: "bold",
      cellPadding: 1.6,
    },
    alternateRowStyles: { fillColor: LIGHT_BG },
    columnStyles,
    theme: "grid",
    margin: { left: marginLeft, right: marginLeft },
    tableWidth: matrixWidth,
    didParseCell: (data) => {
      if (data.section === "head" && data.row.index === 1) {
        data.cell.styles.fillColor = TEAL;
      }
      if (data.section === "body" && data.column.index >= 2) {
        data.cell.styles.textColor = gradeColor(data.cell.raw);
        if (data.column.index === gradeColIndex) data.cell.styles.fontStyle = "bold";
      }
    },
  });

  return doc.lastAutoTable.finalY + 5;
};

// ─── Static co-scholastic / attitude & values page ─────────────────────────
// Appended as page 2 of every report card, identical for every student.
// Grade / Height / Weight / Remarks cells are left blank for the class
// teacher to fill in by hand after the PDF is printed.
const addCoScholasticPage = (doc, ctx) => {
  const { pageWidth, pageHeight, NAVY, TEAL, borderMargin, student, sessionYear } = ctx;

  doc.addPage("a4", "l");

  // Outer + inner border, matching page 1
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.6);
  doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);
  doc.setDrawColor(190, 171, 108);
  doc.setLineWidth(0.2);
  doc.rect(
    borderMargin + 1.5,
    borderMargin + 1.5,
    pageWidth - 2 * (borderMargin + 1.5),
    pageHeight - 2 * (borderMargin + 1.5)
  );

  // Title
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text("Co-Scholastic Report", pageWidth / 2, 20, null, null, "center");

  doc.setFontSize(10.5);
  doc.setFont("times", "normal");
  doc.text(
    `${student.studentName ?? "N/A"}   |   Class & Section: ${student.className ?? "N/A"} - ${student.sectionName ?? "N/A"}   |   Session: ${sessionYear}`,
    pageWidth / 2,
    27,
    null,
    null,
    "center"
  );

  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(15, 31, pageWidth - 15, 31);

  const minMargin = 15;
  const tableWidth = pageWidth - 2 * minMargin;
  let y = 36;

  // Renders one titled section (colored label bar + a 3-column table:
  // Area | Descriptive Indicators | blank Grade cell for the teacher).
  const sectionTable = (title, rows) => {
    doc.setFillColor(...NAVY);
    doc.rect(minMargin, y, tableWidth, 6, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("times", "bold");
    doc.setFontSize(9.5);
    doc.text(title, minMargin + 3, y + 4.3);
    doc.setTextColor(0, 0, 0);
    y += 6;

    doc.autoTable({
      startY: y,
      head: [
        [
          { content: "Area", styles: { halign: "center" } },
          { content: "Descriptive Indicators", styles: { halign: "center" } },
          { content: "Grade", styles: { halign: "center" } },
        ],
      ],
      body: rows.map((r) => [r.area, r.indicators, ""]),
      styles: { fontSize: 7.6, font: "times", cellPadding: 1.8, valign: "middle" },
      headStyles: { fillColor: TEAL, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 42, fontStyle: "bold" },
        1: { cellWidth: tableWidth - 42 - 20 },
        2: { cellWidth: 20, halign: "center" },
      },
      theme: "grid",
      margin: { left: minMargin, right: minMargin },
      tableWidth,
    });

    y = doc.lastAutoTable.finalY + 4;
  };

  sectionTable("2(D): Attitude and Values", [
    {
      area: "Attitude Towards\nSchool Mates",
      indicators:
        "Respects opposite gender, sensitive and supportive towards peers, expresses ideas and opinions freely, friendly with classmates, receptive to ideas and opinions of others.",
    },
    {
      area: "Attitude Towards\nSchool Programmes",
      indicators:
        "Shoulders responsibility happily, renders social work enthusiastically, a good team member, has an urge to be groomed and shows loyalty towards house duties, takes suggestions and criticism in a right and positive manner and makes an effort to improve.",
    },
    {
      area: "Attitude Towards\nTeachers",
      indicators:
        "Courteous to teachers, respects teachers' instructions, feels free to ask questions, is helpful to teachers, respects norms and rules of the school.",
    },
    {
      area: "Value Systems",
      indicators:
        "Respects opposite gender, respects teachers, kind and helpful towards classmates, treasures the belongings of the school, shows compassion.",
    },
  ]);

  sectionTable("Co-Scholastic Activities", [
    {
      area: "Organizational &\nLeadership Skills (Clubs)",
      indicators:
        "Can organize workgroups in short time, delivers assigned jobs with responsibility, exhibits collaboration and coordination skills while executing the tasks, organises events, plans and manages different kinds of events.",
    },
    {
      area: "Scientific Skills",
      indicators: "Has a systematic approach for science related events, is a keen observer, makes use of technology.",
    },
  ]);

  sectionTable("Health and Physical Education", [
    {
      area: "Sports /\nIndigenous Sports",
      indicators:
        "Demonstrate physical fitness and agility, demonstrates sportsmanship, follows rules of the games and sports, organises and provides leadership in this area.",
    },
    {
      area: "Yoga",
      indicators:
        "Displays courage and determination, demonstrates flexibility of the body, follows all safety norms of games and sports, has undergone training and coaching in yoga, takes interest in wellness.",
    },
  ]);

  // ── Health Status + Teacher's Remarks, side by side ──────────────────────
  const healthWidth = tableWidth * 0.42;
  const remarksWidth = tableWidth - healthWidth - 6;
  const healthLeft = minMargin;
  const remarksLeft = minMargin + healthWidth + 6;
  const rowStartY = y;

  doc.autoTable({
    startY: rowStartY,
    head: [[{ content: "Health Status", colSpan: 2, styles: { halign: "center" } }]],
    body: [
      ["Height (cms)", ""],
      ["Weight (kgs)", ""],
    ],
    styles: { fontSize: 8.5, font: "times", cellPadding: 2.2 },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: healthWidth * 0.5, fontStyle: "bold" }, 1: { cellWidth: healthWidth * 0.5 } },
    theme: "grid",
    margin: { left: healthLeft },
    tableWidth: healthWidth,
  });
  const healthFinalY = doc.lastAutoTable.finalY;

  doc.autoTable({
    startY: rowStartY,
    head: [[{ content: "Teacher's Remarks", styles: { halign: "center" } }]],
    body: [[""]],
    styles: { fontSize: 8.5, font: "times", cellPadding: 2.2, minCellHeight: 16 },
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
    theme: "grid",
    margin: { left: remarksLeft },
    tableWidth: remarksWidth,
  });
  const remarksFinalY = doc.lastAutoTable.finalY;

  y = Math.max(healthFinalY, remarksFinalY) + 5;

  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated on ${new Date().toLocaleString()}`,
    pageWidth / 2,
    pageHeight - borderMargin - 3,
    null,
    null,
    "center"
  );
  doc.setTextColor(0, 0, 0);
};

const TeacherGeneratedResult = () => {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [session, setSession] = useState("");
  const [examType, setExamType] = useState("");
  const [className, setClassName] = useState("");
  const [classes, setClasses] = useState([]);
  const [section, setSection] = useState("");
  const [sections, setSections] = useState([]);
  const [resultData, setResultData] = useState([]);
  // Rank lookup for the currently loaded class/section, built from
  // /Result/ClassRanking. Keyed by studentId so both the on-screen table
  // and the PDF can show "Rank X of Y" without an extra call per student.
  const [rankingMap, setRankingMap] = useState({});
  const [totalStudentsInClass, setTotalStudentsInClass] = useState(0);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [formId, setFormId] = useState(null);
  const [error, setError] = useState(null);

  // ─── Search + pagination for the on-screen results table ────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ─── Fee-check-before-print state ────────────────────────────────────────
  // Clicking "Print" first hits StudentResultWithFeeCheck. If dues are
  // clear (canViewResult: true) we go straight to PDF generation. If dues
  // are pending, we show a confirmation modal with the due breakdown.
  const [isFeeCheckLoading, setIsFeeCheckLoading] = useState(false);
  const [feeCheckStudentId, setFeeCheckStudentId] = useState(null);
  const [feeCheckDialogOpen, setFeeCheckDialogOpen] = useState(false);
  const [feeCheckData, setFeeCheckData] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const sortedResultData = [...resultData].sort((a, b) => {
    const rankA = rankingMap[a.studentId]?.rank ?? Infinity;
    const rankB = rankingMap[b.studentId]?.rank ?? Infinity;
    return rankA - rankB;
  });

  const filteredResultData = sortedResultData.filter(
    (student) =>
      student.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.rollNo?.toString().includes(searchTerm)
  );

  const paginatedResultData = filteredResultData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ─── Fetch Form ID ──────────────────────────────────────────────────────────
const fetchFormId = async () => {
  try {
    const response = await fetch(
      "https://wsmsv1.aidigitalsec.com/api/ExclusivePermission/Form_Authorizer",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ moduleId: 23 }),
      }
    );
    const data = await response.json();
    if (response.ok) {
      // Match the exact formName from the API response
      const resultForm = data.find((form) => form.formName === "Download Result");
      if (resultForm) {
        setFormId(resultForm.formId); // Set formId to 156
      } else {
        setError("Download Result form not found in response");
        setIsLoading(false);
      }
    } else {
      setError("Failed to fetch form ID");
      setOpenSnackbar(true);
      setSnackbarMessage("Failed to fetch form ID");
      setIsLoading(false);
    }
  } catch (error) {
    console.error("Error fetching form ID:", error);
    setError("Error fetching form ID");
    setOpenSnackbar(true);
    setSnackbarMessage("Error fetching form ID");
    setIsLoading(false);
  }
};

  // ─── Fetch Permissions ──────────────────────────────────────────────────────
  const fetchPermissions = async () => {
    if (!formId) return;
    try {
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ formId }),
      });
      const data = await response.json();
      if (response.ok && data.length > 0) {
        setPermissions(data[0]);
      } else {
        setPermissions({ ...permissions, isPermission: 0 });
        setError("Failed to fetch permissions");
        setOpenSnackbar(true);
        setSnackbarMessage("Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setError("Error fetching permissions");
      setOpenSnackbar(true);
      setSnackbarMessage("Error fetching permissions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setResultData([]);
    setRankingMap({});
    setTotalStudentsInClass(0);
    setSearchTerm("");
    setPage(0);
  }, [session, className, section]);

  const fetchSessions = async () => {
    try {
      setLoadingBarProgress(30);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ClassPromotion/GetFinancialYear",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
        }
      );
      if (!response.ok) { setLoadingBarProgress(0); return; }
      setLoadingBarProgress(100);
      const data = await response.json();
      if (Array.isArray(data)) {
        setSessions(data);
        // Auto-select the latest session (highest financialYearID)
        if (data.length > 0) {
          const latest = data.reduce((max, s) =>
            s.financialYearID > max.financialYearID ? s : max
          );
          setSession(latest.financialYearID);
        }
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  const fetchExamTypes = async () => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ExamList`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setExamTypes(data.map((item) => ({ id: item.examTypeId, name: item.examType })));
      }
    } catch (error) {
      console.error("Error fetching exam types:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setClasses(data.map((item) => ({ id: item.classId, name: item.className })));
      }
    } catch (error) {
      console.error("Error fetching class names:", error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setSections(data.map((item) => ({ id: item.sectionId, name: item.sectionName })));
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  // ─── Class ranking (for both the on-screen table and the PDF) ─────────────
  const fetchClassRanking = async (sessionId, classId, sectionId) => {
    try {
      const response = await fetch(`${apiUrl}/Result/ClassRanking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ sessionId, examTypeId: 0, classId, sectionId }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching class ranking:", error);
      return [];
    }
  };

  const handleClassChange = (event) => {
    const selectedClass = event.target.value;
    setClassName(selectedClass);
    if (selectedClass) fetchSections(selectedClass);
    else setSections([]);
  };

  const handleGetResult = async () => {
    if (!session || !className || !section) {
      setSnackbarMessage("Please select all fields!");
      setOpenSnackbar(true);
      return;
    }
    try {
      setIsLoading(true);
      const [resultsResponse, rankingList] = await Promise.all([
        fetch("https://wsmsv1.aidigitalsec.com/api/Result/GetStudentResultsGenerated", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            sessionId: session,
            examTypeId: 0,
            classId: className,
            sectionId: section,
          }),
        }),
        fetchClassRanking(session, className, section),
      ]);

      if (!resultsResponse.ok) throw new Error(`HTTP error! Status: ${resultsResponse.status}`);
      const data = await resultsResponse.json();

      const map = {};
      rankingList.forEach((r) => {
        map[r.studentId] = r;
      });
      setRankingMap(map);
      setTotalStudentsInClass(rankingList.length);

      if (Array.isArray(data) && data.length > 0) {
        setResultData(data);
      } else {
        setSnackbarMessage("No data available for the selected class.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error fetching result data.");
      setOpenSnackbar(true);
      console.error("Error fetching result data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setClassName("");
    setSection("");
    setSections([]);
    setResultData([]);
    setRankingMap({});
    setTotalStudentsInClass(0);
    setSearchTerm("");
    setPage(0);
  };

  // ─── FEE CHECK — runs before the print/PDF pipeline ────────────────────────
  // Hits StudentResultWithFeeCheck first.
  //   - canViewResult: true  → dues are clear, go straight to PDF generation.
  //   - canViewResult: false → dues are pending, show a confirmation modal
  //     with the due breakdown.
  const handlePrintClick = async (studentId) => {
    if (!session) {
      setSnackbarMessage("Missing session — please reload and try again.");
      setOpenSnackbar(true);
      return;
    }
    setFeeCheckStudentId(studentId);
    setIsFeeCheckLoading(true);
    try {
      const response = await fetch(`${apiUrl}/Result/StudentResultWithFeeCheck`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data.canViewResult) {
        // Dues are clear — proceed directly to PDF generation.
        setIsFeeCheckLoading(false);
        setFeeCheckStudentId(null);
        fetchStudentProfile(studentId);
      } else {
        // Dues are pending — surface the breakdown.
        setFeeCheckData(data);
        setFeeCheckDialogOpen(true);
        setIsFeeCheckLoading(false);
      }
    } catch (error) {
      console.error("Error checking fee status:", error);
      setSnackbarMessage("Error checking fee status. Please try again.");
      setOpenSnackbar(true);
      setIsFeeCheckLoading(false);
      setFeeCheckStudentId(null);
    }
  };

  const handleCancelFeeCheck = () => {
    setFeeCheckDialogOpen(false);
    setFeeCheckData(null);
    setFeeCheckStudentId(null);
  };

  const handlePrintAnyways = () => {
    const studentId = feeCheckStudentId;
    setFeeCheckDialogOpen(false);
    setFeeCheckData(null);
    setFeeCheckStudentId(null);
    if (studentId) fetchStudentProfile(studentId);
  };

  // ─── MAIN PRINT HANDLER ─────────────────────────────────────────────────────
  // Flow:
  //   1. GET student profile  → objData (profile), totalAttendancList[0] (yearly attendance)
  //   2. GET exam results     → results[] (per-subject, per-exam-type rows), totalMarks1[] (per-subject totals)
  //   3. GET exam criteria    → list of examType headers
  //   4. For each exam type, GET sub-categories → sub-exam column names
  //   5. Build PDF from all collected data
  //
  // DATA FIXES applied here:
  //   - normKey() unifies the "--"/"" sub-exam placeholder mismatch between
  //     GetExamResults and GetExamSubCategory_Result — this was the actual
  //     cause of the duplicated/blank "Term 1" column in the PDF.
  //   - The API returns a "ghost" subject row (subjectId: 0, subjectName: "")
  //     which is really a per-exam-type total, not a real subject — it is
  //     filtered out everywhere (subjectTotals, marksData).
  //   - Subject names are sanitized (e.g. "Hindi`" -> "Hindi").
  //   - Exam-wise totals (Term 1 / Term 2 subtotal) are computed directly
  //     from `results` — NOT from the `totalMarks` array, which has no
  //     examType field on its rows and can't be labeled reliably.
  //   - A real overall Pass/Fail status is computed (any subject grade E/F ⇒ FAIL).
  const fetchStudentProfile = async (studentId) => {
    try {
      setIsPdfLoading(true);
      setLoadingBarProgress(30);

      // ── Step 1: Student profile + attendance ──────────────────────────────
      const profileResponse = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ yearId: session, studentId }),
      });
      if (!profileResponse.ok) throw new Error("Failed to fetch student profile");
      const profileData = await profileResponse.json();

      const studentProfile = profileData.objData;
      const studentAttendance = profileData.totalAttendancList?.[0] ?? null;

      // ── Step 2: Exam results ──────────────────────────────────────────────
      const resultsResponse = await fetch(`${apiUrl}/Result/GetExamResults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });
      if (!resultsResponse.ok) {
        setLoadingBarProgress(0);
        throw new Error("Failed to fetch exam results");
      }
      setLoadingBarProgress(70);
      const resultsData = await resultsResponse.json();

      const results = resultsData.results ?? [];

      // FIX: drop the "ghost" per-exam-type total row (subjectId 0 / blank name)
      // and sanitize real subject names.
      const subjectTotals = (resultsData.totalMarks1 ?? [])
        .filter((s) => s.subjectName && sanitizeName(s.subjectName) !== "")
        .map((s) => ({ ...s, subjectName: sanitizeName(s.subjectName) }));

      const grossTotal = resultsData.grosstotalMarks?.[0] ?? {};
      const overallGrade = grossTotal.totalGrade ?? "N/A";
      const totalPercentage =
        grossTotal.overallPercentage ?? grossTotal.averagePercentage ?? "N/A";

      const teacherRemarks = results[0]?.teacherRemarks ?? "N/A";

      const subjectNames = subjectTotals.map((s) => s.subjectName);

      // Real overall result status — FAIL if any subject's grade is E/F
      const hasFailingGrade = subjectTotals.some((s) =>
        ["E", "F"].includes((s.totalGrade || "").toString().trim().toUpperCase())
      );
      const resultStatus = hasFailingGrade ? "FAIL" : "PASS";

      // Exam-wise totals (Term 1 / Term 2 ...), derived from `results` since
      // `totalMarks` rows carry no examType/examTypeId to key off of.
      const examWiseTotals = Object.values(
        results.reduce((acc, r) => {
          if (r.subjectId === 0) return acc; // skip ghost total row
          const key = r.examTypeId;
          if (!acc[key]) acc[key] = { examType: normKey(r.examType), obtained: 0, max: 0 };
          acc[key].obtained += Number(r.obtainedMarks) || 0;
          acc[key].max += Number(r.maxMarks) || 0;
          return acc;
        }, {})
      ).map((e) => ({ ...e, percentage: e.max ? +((e.obtained / e.max) * 100).toFixed(2) : 0 }));

      // ── Step 3: Exam criteria ─────────────────────────────────────────────
      const examCriteriaResponse = await fetch(
        `${apiUrl}/Result/GetExamCriteria_result`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ studentId }),
        }
      );
      if (!examCriteriaResponse.ok) throw new Error("Failed to fetch exam criteria");
      const examCriteriaData = await examCriteriaResponse.json();

      // ── Step 4: Build examHeadersData using GetExamSubCategory_Result API ──
      const subCategoryPromises = examCriteriaData.map(async (exam) => {
        const subCategoryResponse = await fetch(
          `${apiUrl}/Result/GetExamSubCategory_Result`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({
              studentId,
              examTypeId: exam.examCategoryId,
            }),
          }
        );

        if (!subCategoryResponse.ok) {
          console.error(`Failed to fetch sub-category for exam type ${exam.examType}`);
          return {
            examType: normKey(exam.examType),
            examCategoryId: exam.examCategoryId,
            subCategories: [],
          };
        }

        const subCategoryData = await subCategoryResponse.json();

        const subCategories = subCategoryData.map((sub) => {
          // normKey collapses "--" (GetExamSubCategory_Result's placeholder)
          // and "" (GetExamResults' placeholder) to the same key, so a
          // single-category exam type (Term 1) produces exactly ONE column
          // instead of the previous duplicate "--"/"Marks" pair.
          const subExamName = normKey(sub.subExam);
          const matchingResult = results.find(
            (r) => r.examTypeId === exam.examCategoryId && normKey(r.subExam) === subExamName
          );
          return {
            name: subExamName,
            maxMarks: matchingResult?.maxMarks ?? sub.maxMarks ?? "",
            minMarks: matchingResult?.minMarks ?? sub.minMarks ?? "",
          };
        });

        return {
          examType: normKey(exam.examType),
          examCategoryId: exam.examCategoryId,
          subCategories,
        };
      });

      const examHeadersData = await Promise.all(subCategoryPromises);

      // ── Step 4b: Safety net — never drop a recorded mark ────────────────────
      // examHeadersData (table COLUMNS) comes from GetExamCriteria_result /
      // GetExamSubCategory_Result, which is a *separate* source of truth from
      // the actual `results` records. If that criteria API is missing an exam
      // type/sub-exam that a student actually has marks for (stale config,
      // retired exam type, etc.), the mark exists in marksData but has no
      // column to render in. To guarantee every real mark is shown, we
      // backfill any examType/subExam combo found in `results` that isn't
      // already covered by the criteria-based headers.
      const existingCombos = new Set();
      examHeadersData.forEach((exam) => {
        exam.subCategories.forEach((sub) => {
          existingCombos.add(`${exam.examType}|||${sub.name}`);
        });
      });

      results.forEach((r) => {
        if (r.subjectId === 0) return; // skip the ghost total row
        const examTypeKey = normKey(r.examType);
        const subExamKey = normKey(r.subExam);
        const comboKey = `${examTypeKey}|||${subExamKey}`;
        if (existingCombos.has(comboKey)) return;

        existingCombos.add(comboKey);
        let examEntry = examHeadersData.find((e) => e.examType === examTypeKey);
        if (!examEntry) {
          examEntry = { examType: examTypeKey, examCategoryId: r.examTypeId, subCategories: [] };
          examHeadersData.push(examEntry);
        }
        examEntry.subCategories.push({
          name: subExamKey,
          maxMarks: r.maxMarks ?? "",
          minMarks: r.minMarks ?? "",
        });
      });

      // ── Step 5: Build marks lookups (keys normalized, not just trimmed) ────
      // marksData    -> obtained marks, used by the regular numeric matrix table.
      // marksGradeData -> the backend's own per-row grade, used by the
      //   pre-primary "Academic performance: Scholastic Areas" indicator
      //   table (same grade field/scale shown on regular report cards —
      //   NOT computed from marks here).
      const marksData = {};
      const marksGradeData = {};
      results.forEach((r) => {
        // Skip the same ghost row here too (subjectId 0 / blank name)
        if (r.subjectId !== 0 && r.subjectName) {
          const subjectKey = sanitizeName(r.subjectName);
          const examTypeKey = normKey(r.examType);
          const subExamKey = normKey(r.subExam);

          if (!marksData[subjectKey]) marksData[subjectKey] = {};
          if (!marksData[subjectKey][examTypeKey]) marksData[subjectKey][examTypeKey] = {};
          marksData[subjectKey][examTypeKey][subExamKey] = r.obtainedMarks;

          if (!marksGradeData[subjectKey]) marksGradeData[subjectKey] = {};
          if (!marksGradeData[subjectKey][examTypeKey]) marksGradeData[subjectKey][examTypeKey] = {};
          marksGradeData[subjectKey][examTypeKey][subExamKey] = r.grade ?? r.totalGrade ?? "";
        }
      });

      // ── Step 6: Build subjectTotalsMap ───────────────────────────────────
      const subjectTotalsMap = {};
      subjectTotals.forEach((s) => {
        subjectTotalsMap[s.subjectName] = s;
      });

      const updatedStudentProfile = {
        ...studentProfile,
        teacherRemarks,
        grade: overallGrade,
        totalPercentage,
        resultStatus,
      };

      const selectedSession = sessions.find((s) => s.financialYearID === session);
      const sessionYear = selectedSession?.finanacialYear ?? "N/A";

      // Rank + class size, from the ranking list already fetched in handleGetResult
      const rankInfo = rankingMap[studentId];
      const studentRank = rankInfo?.rank ?? null;

      setLoadingBarProgress(100);

      handlePrint(
        updatedStudentProfile,
        studentAttendance,
        subjectNames,
        examHeadersData,
        marksData,
        marksGradeData,
        subjectTotalsMap,
        sessionYear,
        examWiseTotals,
        studentRank,
        totalStudentsInClass
      );
    } catch (error) {
      setIsPdfLoading(false);
      setLoadingBarProgress(0);
      alert("Error generating PDF: " + error.message);
      console.error("API request error:", error);
    }
  };

  // ─── PDF GENERATION — single-page report-card layout (+ static page 2) ────
  const handlePrint = async (
    student,
    attendance,         // totalAttendancList[0] — yearly aggregate
    subjectNames,        // unique, sanitized subject names (from totalMarks1)
    examHeadersData,      // [{ examType, subCategories: [{ name, maxMarks, minMarks }] }]
    marksData,            // marksData[subject][examType][subExam] = obtainedMarks
    marksGradeData,       // marksGradeData[subject][examType][subExam] = backend grade
    subjectTotalsMap,     // totalMarks1 keyed by sanitized subjectName
    sessionYear,
    examWiseTotals,        // [{ examType, obtained, max, percentage }]
    studentRank,           // number | null
    totalStudents          // number
  ) => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();   // 297
      const pageHeight = doc.internal.pageSize.getHeight(); // 210

      const NAVY = [11, 31, 64];
      const TEAL = [0, 128, 128];
      const GOLD = [190, 150, 40];
      const LIGHT_BG = [245, 247, 250];

      // LKG / UKG / Nursery get the "Academic performance: Scholastic Areas"
      // report card layout instead of the regular numeric marks matrix.
      const isPrePrimary = isPrePrimaryClass(student.className);

      // Outer border
      const borderMargin = 8;
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.6);
      doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);

      // Thin inner accent border for a "certificate" feel
      doc.setDrawColor(190, 171, 108); // gold
      doc.setLineWidth(0.2);
      doc.rect(
        borderMargin + 1.5,
        borderMargin + 1.5,
        pageWidth - 2 * (borderMargin + 1.5),
        pageHeight - 2 * (borderMargin + 1.5)
      );

      // ── Logo & School Header ──────────────────────────────────────────────
      try {
        const placeholderLogo =
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=";
    doc.setFillColor(255, 255, 255);
        doc.rect(12, 16, 22, 22, "F");
        doc.addImage(placeholderLogo, "PNG", 14, 18, 18, 18);

        const schoolResponse = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({}),
          }
        );
        if (!schoolResponse.ok) throw new Error("School API failed");
        const apiData = await schoolResponse.json();
        const address = apiData?.[0]?.address ?? "N/A";
        const phoneNo = apiData?.[0]?.phoneNo ?? "N/A";
        const schoolName = apiData?.[0]?.schoolName ?? "N/A";

        doc.setFont("times", "bold");
        doc.setFontSize(19);
        doc.text(schoolName.replace(/['"]+/g, ""), pageWidth / 2, 21, null, null, "center");

        doc.setFontSize(9.5);
        doc.setFont("times", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(address.replace(/['"]+/g, ""), pageWidth / 2, 28, null, null, "center");
        doc.text("Contact No.: " + phoneNo.replace(/['"]+/g, ""), pageWidth / 2, 34, null, null, "center");
        doc.setTextColor(0, 0, 0);

        const logoImg = apiData?.[0]?.logoImg;
        if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
          let cleanedBase64 = logoImg.replace(/^data:image\/[a-zA-Z]+;base64,/, "").replace(/\s|\n/g, "");
          cleanedBase64 += "=".repeat((4 - (cleanedBase64.length % 4)) % 4);
          if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
            const logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
            const apiImg = new Image();
            await new Promise((res, rej) => {
              apiImg.onload = res;
              apiImg.onerror = () => rej(new Error("Failed to load logo"));
              apiImg.src = logoDataUrl;
            });
           doc.setFillColor(255, 255, 255);
            doc.rect(12, 16, 22, 22, "F");
            doc.addImage(logoDataUrl, "PNG", 13.5, 18, 19, 19);
          }
        }
      } catch (err) {
        console.error("Logo/school header error:", err.message);
      }

      // PASS / FAIL stamp — top right
      const isPass = (student.resultStatus ?? "").toUpperCase() !== "FAIL";
      const stampColor = isPass ? [22, 130, 60] : [178, 34, 34];
      doc.setFillColor(...stampColor);
      doc.roundedRect(pageWidth - 46, 13, 30, 11, 2, 2, "F");
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(isPass ? "PASS" : "FAIL", pageWidth - 31, 20.5, null, null, "center");

      // RANK badge — directly below the PASS/FAIL stamp
      const rankLabel = studentRank ? `RANK - ${studentRank}` : "RANK N/A";
      doc.setFillColor(...GOLD);
      doc.roundedRect(pageWidth - 46, 25.5, 30, 9, 2, 2, "F");
      doc.setFontSize(9);
      doc.text(rankLabel, pageWidth - 31, 31.3, null, null, "center");
      doc.setTextColor(0, 0, 0);

      // Divider
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.9);
      doc.line(15, 40, pageWidth - 15, 40);

      // Report title + student name
      doc.setFontSize(11.5);
      doc.setFont("times", "bold");
      doc.text(`Progress Report ${sessionYear}`, pageWidth / 2, 46, null, null, "center");

      doc.setFontSize(14);
      doc.text(`${student.studentName ?? "N/A"}`, pageWidth / 2, 54, null, null, "center");
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - 35, 57, pageWidth / 2 + 35, 57);

      // ── Student details panel ──────────────────────────────────────────────
      const panelY = 61;
      const panelH = 24;
      doc.setFillColor(...LIGHT_BG);
      doc.roundedRect(15, panelY, pageWidth - 30, panelH, 2, 2, "F");

      doc.setFont("times", "normal");
      doc.setFontSize(9.5);
      const labelWidth = 30;
      const colLeft = 20;
      const colRight = pageWidth / 2 + 5;

      const leftDetails = [
        ["Father's Name:", student.fathersName ?? "N/A"],
        ["Mother's Name:", student.mothersName ?? "N/A"],
      ];
      const rightDetails = [
        ["Class & Section:", `${student.className ?? "N/A"} - ${student.sectionName ?? "N/A"}`],
        ["Roll No.:", String(student.rollNo ?? "N/A")],
      ];
      const thirdCol = [
        ["Admission No.:", String(student.admissionNo ?? "N/A")],
        ["Date of Birth:", student.studentDOB ?? "N/A"],
      ];

      const lineH = 8;
      let y1 = panelY + 8;
      leftDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colLeft, y1);
        doc.setFont("times", "normal");
        doc.text(String(value), colLeft + labelWidth, y1);
        y1 += lineH;
      });

      let y2 = panelY + 8;
      rightDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colRight, y2);
        doc.setFont("times", "normal");
        doc.text(String(value), colRight + labelWidth, y2);
        y2 += lineH;
      });

      const colThird = pageWidth - 95;
      let y3 = panelY + 8;
      thirdCol.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colThird, y3);
        doc.setFont("times", "normal");
        doc.text(String(value), colThird + labelWidth, y3);
        y3 += lineH;
      });

      // ── Marks section ────────────────────────────────────────────────────
      // Pre-primary (LKG/UKG/Nursery) gets the "Academic performance:
      // Scholastic Areas" layout (summary strip + grouped indicator/grade
      // table). Every other class keeps the regular numeric marks matrix.
      let lastY = panelY + panelH + 5;
      const minMargin = 15;

     if (isPrePrimary) {
        const overallObtained = subjectNames.reduce(
          (sum, s) => sum + (Number(subjectTotalsMap[s]?.totalObtainedMarks) || 0),
          0
        );
        const overallMax = subjectNames.reduce(
          (sum, s) => sum + (Number(subjectTotalsMap[s]?.totalMaxMarks) || 0),
          0
        );

        lastY = addPrePrimaryScholasticSection(doc, {
          pageWidth,
          minMargin,
          startY: lastY,
          NAVY,
          TEAL,
          LIGHT_BG,
          subjectNames,
          examHeadersData,
          marksGradeData,
          subjectTotalsMap,
          overallMarksText: overallMax ? `${overallObtained} / ${overallMax}` : "N/A",
          overallPercentageText: formatPercent(student.totalPercentage),
          overallGradeText: student.grade ?? "N/A",
        });
      } else if (examHeadersData.length > 0 && subjectNames.length > 0) {
        // ── Marks Table (regular, non pre-primary) ────────────────────────
        // Columns: S.No | Subject | [ExamType > SubExam ...] | Total (Obt/Max) | Grade
        const headerRow1 = [
          { content: "S.No", rowSpan: 2, styles: { halign: "center" } },
          { content: "Subject", rowSpan: 2, styles: { halign: "center" } },
        ];
        const headerRow2 = [];

        examHeadersData.forEach((exam) => {
          headerRow1.push({
            content: exam.examType,
            colSpan: exam.subCategories.length,
            styles: { halign: "center" },
          });
          exam.subCategories.forEach((sub) => {
            // sub.name is "" for single-category exam types (e.g. Term 1)
            // after the normKey fix — show a sensible label instead of blank.
            const label = sub.name ? sub.name : "Marks";
            headerRow2.push({
              content: `${label}\n(Max:${sub.maxMarks})`,
              styles: { halign: "center" },
            });
          });
        });

        headerRow1.push(
          { content: "Total\n(Obt/Max)", rowSpan: 2, styles: { halign: "center" } },
          { content: "Grade", rowSpan: 2, styles: { halign: "center" } }
        );

        const tableHeaders = [headerRow1, headerRow2];

        const tableData = subjectNames.map((subject, idx) => {
          const row = [idx + 1, subject];

          examHeadersData.forEach((exam) => {
            exam.subCategories.forEach((sub) => {
              const mark = marksData[subject]?.[exam.examType]?.[sub.name];
              row.push(formatMarks(mark));
            });
          });

          const totals = subjectTotalsMap[subject];
          row.push(`${formatMarks(totals?.totalObtainedMarks)} / ${formatMarks(totals?.totalMaxMarks)}`);
          const overallSubjectGrade =
            totals?.totalGrade ??
            (totals?.totalMaxMarks
              ? gradeFromPercent((Number(totals.totalObtainedMarks) / Number(totals.totalMaxMarks)) * 100)
              : "--");
          row.push(overallSubjectGrade);

          return row;
        });

        const fixedColsCount = 4; // S.No + Subject + Total + Grade
        const totalColumns =
          2 + examHeadersData.reduce((sum, e) => sum + e.subCategories.length, 0) + 2;
        const gradeColIndex = totalColumns - 1;

        const snoColW = 10;
        const subjectColW = 30;
        const totalColW = 24;
        const gradeColW = 16;
        const available = pageWidth - 2 * minMargin - snoColW - subjectColW - totalColW - gradeColW;
        const subExamColW = Math.min(20, available / (totalColumns - fixedColsCount));
        const tableWidth = snoColW + subjectColW + (totalColumns - fixedColsCount) * subExamColW + totalColW + gradeColW;
        const marginLeft = (pageWidth - tableWidth) / 2;

        const columnStyles = {
          0: { cellWidth: snoColW },
          1: { cellWidth: subjectColW },
        };
        let colIdx = 2;
        examHeadersData.forEach((exam) => {
          exam.subCategories.forEach(() => {
            columnStyles[colIdx++] = { cellWidth: subExamColW };
          });
        });
        columnStyles[colIdx] = { cellWidth: totalColW };
        columnStyles[colIdx + 1] = { cellWidth: gradeColW };

        doc.autoTable({
          startY: lastY,
          head: tableHeaders,
          body: tableData,
          styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 1.6 },
          headStyles: {
            fillColor: NAVY,
            textColor: [255, 255, 255],
            lineWidth: 0.5,
            font: "times",
            fontStyle: "bold",
            cellPadding: 1.6,
          },
          alternateRowStyles: { fillColor: LIGHT_BG },
          columnStyles,
          theme: "grid",
          margin: { left: marginLeft, right: marginLeft },
          tableWidth,
          didParseCell: (data) => {
            if (data.section === "head" && data.row.index === 1) {
              data.cell.styles.fillColor = TEAL;
            }
            if (data.section === "body" && data.column.index === gradeColIndex) {
              data.cell.styles.textColor = gradeColor(data.cell.raw);
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        lastY = doc.lastAutoTable.finalY + 5;
      }

      // ── Remarks (+ Overall Grade for non pre-primary) ────────────────────
      // Pre-primary already shows Overall Marks/Percentage/Grade in the
      // Scholastic Areas summary strip above, so it's not repeated here.
      doc.setFontSize(9.5);
      doc.setFont("times", "bold");
      doc.text("Remarks:", 15, lastY);
      doc.setFont("times", "normal");
      doc.text(`${student.teacherRemarks ?? "N/A"}`, 33, lastY);

      if (!isPrePrimary) {
        const gradeText = `${student.grade ?? "N/A"} (${formatPercent(student.totalPercentage)})`;
        const rightMargin = 15;
        doc.setFont("times", "bold");
        doc.text("Overall Grade:", pageWidth - rightMargin - doc.getTextWidth(gradeText) - 32, lastY);
        doc.setFont("times", "normal");
        doc.setTextColor(...gradeColor(student.grade));
        doc.text(gradeText, pageWidth - rightMargin, lastY, null, null, "right");
        doc.setTextColor(0, 0, 0);
      }

      lastY += 6;

      // ── Exam-wise totals strip (single line) ─────────────────────────────
      // Computed from `results` (has examTypeId/examType on every row) — the
      // API's own `totalMarks` array has no such field, so it can't be
      // labeled reliably and is intentionally not used here. Hidden for
      // pre-primary classes since it's a numeric-marks summary.
      if (!isPrePrimary && examWiseTotals?.length > 0) {
        doc.setFillColor(...LIGHT_BG);
        doc.roundedRect(15, lastY - 4, pageWidth - 30, 8, 1.5, 1.5, "F");
        doc.setFontSize(8.5);
        doc.setFont("times", "bold");
        const summaryText = examWiseTotals
          .map(
            (e) =>
              `${e.examType}:  ${formatMarks(e.obtained)}/${formatMarks(e.max)}  (${formatPercent(
                e.percentage
              )})`
          )
          .join("        |        ");
        doc.text(summaryText, pageWidth / 2, lastY + 1, null, null, "center");
        doc.setFont("times", "normal");
        lastY += 10;
      } else {
        lastY += 4;
      }

      // ── Attendance + Grading Scale, side by side to save vertical space ──
      const sideBySideY = lastY;
      const totalWidth = pageWidth - 2 * minMargin;
      const gap = 6;
      const attendanceWidth = totalWidth * 0.42;
      const gradingWidth = totalWidth - attendanceWidth - gap;
      const attendanceLeft = minMargin;
      const gradingLeft = minMargin + attendanceWidth + gap;

      const attendanceHeaders = [
        [
          { content: "Total Days", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Present", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Absent", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Attendance %", styles: { halign: "center", font: "times", fontStyle: "bold" } },
        ],
      ];
      const attendanceData = [
        [
          attendance?.totalDays ?? "N/A",
          attendance?.presentCount ?? "N/A",
          attendance?.absentCount ?? "N/A",
          attendance?.attendancePercentage != null ? formatPercent(attendance.attendancePercentage) : "N/A",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: attendanceHeaders,
        body: attendanceData,
        styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 2 },
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
        theme: "grid",
        margin: { left: attendanceLeft },
        tableWidth: attendanceWidth,
      });
      const attendanceFinalY = doc.lastAutoTable.finalY;

      // Grading scale — school-wide policy (used for the regular report):
      //   90 & above => A+, 80-89 => A, 70-79 => B+, 60-69 => B, 50-59 => C, below 50 => F
      // (Pre-primary uses its own scale, shown in the Scholastic Areas
      // section above instead of here.)
      const gradingHeaders = [
        [
          {
            content: "Marks %",
            styles: { halign: "center", fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5, font: "times" },
          },
          "90 & Above", "80 - 89", "70 - 79", "60 - 69", "50 - 59", "Below 50",
        ],
      ];
      const gradingData = [
        [
          { content: "Grade", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold", fontSize: 7.5, font: "times" } },
          "A+", "A", "B+", "B", "C", "F",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: gradingHeaders,
        body: gradingData,
        styles: { fontSize: 7.5, halign: "center", cellPadding: 2, font: "times" },
        theme: "grid",
        headStyles: { fillColor: TEAL, textColor: [255, 255, 255], font: "times" },
        tableWidth: gradingWidth,
        margin: { left: gradingLeft },
      });
      const gradingFinalY = doc.lastAutoTable.finalY;

      lastY = Math.max(attendanceFinalY, gradingFinalY) + 12;

      // ── Signature Lines & Footer ─────────────────────────────────────────
      doc.setFont("times", "normal");
      doc.setFontSize(9.5);

      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.3);
      doc.line(15, lastY, 75, lastY);
      doc.text("Signature of Class Teacher", 18, lastY + 5);

      doc.line(pageWidth - 75, lastY, pageWidth - 15, lastY);
      doc.text("Signature of Principal", pageWidth - 18, lastY + 5, null, null, "right");

      // Generation timestamp footer, for authenticity/audit trail
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth / 2,
        pageHeight - borderMargin - 3,
        null,
        null,
        "center"
      );
      doc.setTextColor(0, 0, 0);

      // ── PAGE 2 — Co-Scholastic & Attitude/Values (static template) ───────
      // Identical for every student; Grade / Height / Weight / Remarks cells
      // are left blank for the class teacher to fill in by hand.
      addCoScholasticPage(doc, {
        pageWidth,
        pageHeight,
        NAVY,
        TEAL,
        GOLD,
        LIGHT_BG,
        borderMargin,
        student,
        sessionYear,
      });

      setIsPdfLoading(false);
      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (error) {
      setIsPdfLoading(false);
      alert("Error generating PDF: " + error.message);
      console.error("Error generating PDF:", error);
    }
  };

  const handleSessionChange = (event) => setSession(event.target.value);
  const handleExamTypeChange = (event) => setExamType(event.target.value);
  const handleSectionChange = (event) => setSection(event.target.value);

  useEffect(() => { fetchFormId(); }, []);
  useEffect(() => { if (formId) fetchPermissions(); }, [formId]);
  useEffect(() => {
    if (permissions.isPermission && permissions.uRead) {
      fetchSessions();
      fetchExamTypes();
      fetchClasses();
    }
  }, [permissions]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "#d97706" }} />
      </div>
    );
  }

  if (error || !permissions.isPermission) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h4" color="error">You are not authorised to view this page</Typography>
      </div>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: "64px" },
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <LoadingBar progress={loadingBarProgress} color="#d97706" height={5} onLoaderFinished={() => setLoadingBarProgress(0)} />

      {/* ── Filters card ─────────────────────────────────────────────────── */}
      <Card sx={{ mb: 4, boxShadow: 4, borderRadius: 3 }}>
        <CardContent>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "#d97706",
              textAlign: "center",
            }}
          >
            📊 Generated Result{" "}
            <InfoIcon
              onClick={() => setShowUserGuide(true)}
              style={{ opacity: 0.6, cursor: "pointer", verticalAlign: "middle" }}
            />
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Session *</InputLabel>
                <Select value={session} onChange={handleSessionChange} label="Session *">
                  <MenuItem value="">Select Session</MenuItem>
                  {sessions.map((item) => (
                    <MenuItem key={item.financialYearID} value={item.financialYearID}>
                      {item.finanacialYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Class *</InputLabel>
                <Select value={className} onChange={handleClassChange} label="Class *">
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined" disabled={!className}>
                <InputLabel>Section *</InputLabel>
                <Select value={section} onChange={handleSectionChange} label="Section *">
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetResult}
              sx={{
                minWidth: 220,
                backgroundColor: "#d97706",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "#c2410c" },
              }}
            >
              Get Result
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleReset}
              sx={{ minWidth: 220, textTransform: "none" }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Results card ─────────────────────────────────────────────────── */}
      {permissions.uRead ? (
        isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
            <CircularProgress sx={{ color: "#d97706" }} />
            <Typography variant="body1" sx={{ ml: 2 }}>Fetching result data...</Typography>
          </Box>
        ) : resultData.length > 0 ? (
          <Card sx={{ boxShadow: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold", color: "#1976d2" }}>
                🏆 Student Results ({filteredResultData.length})
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or roll number..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 400 }}
                />
              </Box>

              <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#1976d2" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Rank</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Roll No</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Student Name</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Total Marks</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Obtained Marks</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Grade</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedResultData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography variant="h6" color="text.secondary">
                            No students found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedResultData.map((student) => {
                        const rankInfo = rankingMap[student.studentId];
                        const isThisStudentFeeChecking =
                          isFeeCheckLoading && feeCheckStudentId === student.studentId;
                        return (
                          <TableRow key={student.studentId} hover>
                            <TableCell align="center">
                              {rankInfo?.rank ? (
                                <Chip
                                  icon={rankInfo.rank <= 3 ? <EmojiEventsIcon fontSize="small" /> : undefined}
                                  label={rankInfo.rank}
                                  size="small"
                                  color={rankInfo.rank <= 3 ? "warning" : "default"}
                                  sx={{ fontWeight: 700 }}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="center">{student.rollNo}</TableCell>
                            <TableCell>{student.studentName}</TableCell>
                            <TableCell align="center">{student.totalMaxMarks}</TableCell>
                            <TableCell align="center">{student.totalObtainedMarks}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={student.grade ?? "N/A"}
                                size="small"
                                color={gradeChipColor(student.grade)}
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={permissions.uWrite ? "" : "You are not authorised to print results"}>
                                <span>
                                  <Button
                                    onClick={() => handlePrintClick(student.studentId)}
                                    variant="contained"
                                    disabled={!permissions.uWrite || isThisStudentFeeChecking}
                                    sx={{
                                      minWidth: 72,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      backgroundColor: permissions.uWrite ? "#d97706" : "#ccc",
                                      "&:hover": { backgroundColor: permissions.uWrite ? "#c2410c" : "#ccc" },
                                    }}
                                  >
                                    {isThisStudentFeeChecking ? (
                                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    ) : (
                                      "Print"
                                    )}
                                  </Button>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredResultData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ boxShadow: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body1" sx={{ textAlign: "center", py: 4 }}>
                No data to display. Select a session, class and section, then click "Get Result".
              </Typography>
            </CardContent>
          </Card>
        )
      ) : (
        <Card sx={{ boxShadow: 4, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ textAlign: "center", py: 4 }}>
              You are not authorised to view results
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ── User guide dialog ───────────────────────────────────────────── */}
      <Dialog
        open={showUserGuide}
        onClose={() => setShowUserGuide(false)}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", backgroundColor: "#f9fafb" } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(90deg, #d97706 0%, #1976d2 100%)",
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
            borderRadius: "12px 12px 0 0",
            py: 2,
          }}
        >
          Result User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ color: "#d97706", fontWeight: "600", mb: 2, mt: 1, textAlign: "center" }}>
            Quick Steps to Get and Print Result
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyleType: "none",
              padding: 0,
              "& li": { display: "flex", alignItems: "flex-start", mb: 1.5, fontSize: "0.9rem", color: "#333" },
              "& li::before": { content: '"\\2022"', color: "#1976d2", fontWeight: "bold", display: "inline-block", width: "1.5em", mr: 1, fontSize: "1.2rem" },
              "& strong": { margin: "0 0.2em" },
            }}
          >
            <li>Select a <strong>Session</strong> from the dropdown.</li>
            <li>Select a <strong>Class</strong> from the dropdown.</li>
            <li>Select a <strong>Section</strong> from the dropdown.</li>
            <li>Click <strong>Get Result</strong> to load the student list, with class ranks.</li>
            <li>Use the <strong>search box</strong> to quickly find a student by name or roll number.</li>
            <li>Click <strong>Print</strong> next to a student to generate their PDF report. If fees are pending, you'll be shown the due breakdown first.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setShowUserGuide(false)}
            variant="contained"
            sx={{
              backgroundColor: "#d97706",
              "&:hover": { backgroundColor: "#c2410c" },
              borderRadius: "8px",
              px: 4,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Fee-due confirmation modal, shown before printing when dues are pending ── */}
      <Dialog
        open={feeCheckDialogOpen}
        onClose={handleCancelFeeCheck}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#b22222",
            color: "#fff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon /> Unpaid Fee Dues
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="body1" sx={{ mb: 1, mt:1, fontWeight:'bold', color:'red' }}>
            {feeCheckData?.message || "This student's result is blocked due to unpaid fee dues."}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Total Due Amount: ₹{feeCheckData?.totalDueAmount ?? 0}
          </Typography>

          {feeCheckData?.dueMonths?.length > 0 && (
            <Table size="small" sx={{ border: "1px solid #eee" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total Fee</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feeCheckData.dueMonths.map((m, idx) => {
                  const isPaid = m.paidStatus === 1;
                  return (
                    <TableRow key={`${m.monthId}-${idx}`}>
                      <TableCell>{MONTH_NAMES[m.monthId] ?? m.monthId}</TableCell>
                      <TableCell align="right">{m.totalFee}</TableCell>
                      <TableCell align="right">{m.paidAmount}</TableCell>
                      <TableCell align="right">{m.balance}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isPaid ? "Paid" : "Unpaid"}
                          size="small"
                          color={isPaid ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="center">{formatDueDate(m.feePaymentDate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleCancelFeeCheck} variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          {/* <Button onClick={handlePrintAnyways} variant="contained" color="error" sx={{ textTransform: "none" }}>
            Print Anyways
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* ── PDF generation overlay ──────────────────────────────────────── */}
      <Dialog
        open={isPdfLoading}
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
      >
        <DialogContent sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #fde68a",
              borderTop: "4px solid #d97706",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 15px",
            }}
          />
          <Typography>Generating PDF, please wait...</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="warning" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherGeneratedResult;